import { useContext, useEffect, useRef } from 'react';
import { Box } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { Atom01 } from '@kula-ai/icons-core';
import { NavLink } from './NavLink';
import {
  KEducationalTooltip,
  useDismissibleEducationalTooltip,
} from '@/ui-library/components';
import NavigationContext from '../context';

// One-time launch announcement for Ask Kula. Persisted via localStorage so it
// shows once and never again after the user dismisses it.
const VIEWED_KEY = 'viewed_ask_kula';

// The atom's hover motion (design's atom-01): it dips in a touch, spins a full
// turn, then settles back — ending at scale 1 / 360° so the loop is seamless.
// transformBox/origin keep every frame pivoting about the icon's own centre.
const atomSpin = keyframes`
  0%   { transform: scale(1) rotate(0deg); }
  15%  { transform: scale(0.82) rotate(0deg); }
  85%  { transform: scale(0.82) rotate(310deg); }
  100% { transform: scale(1) rotate(360deg); }
`;

// The Ask Kula nav item renders its icon + label in the brand gradient. These
// exact hues are the design's Ask Kula gradient (not theme tokens), so they're
// inlined. An inline <linearGradient> paints the icon's stroke + currentColor
// tint; background-clip paints the label text.
const ASK_KULA_GRADIENT_ID = 'askKulaNavGradient';
const gradientSx = {
  // The atom is an outline: gradient the stroke on both paths, and the fill only
  // on its 12%-opacity tint path (the other path is unfilled — filling it would
  // turn the outline into a solid blob). `&&` doubles the wrapper class so these
  // out-specify the side-nav's own `.app-nav.active .nav path` stroke rule, which
  // otherwise repaints the active item's icon a flat indigo.
  [`&& svg path[stroke="currentColor"]`]: {
    stroke: `url(#${ASK_KULA_GRADIENT_ID})`,
  },
  [`&& svg path[fill="currentColor"]`]: {
    fill: `url(#${ASK_KULA_GRADIENT_ID})`,
  },
  '& .chakra-text': {
    background: 'linear-gradient(180deg, #8A90FF 0%, #B15FDA 100%)',
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  // Spin the atom on hover; fill-box keeps the pivot at the icon's own centre.
  '& .chakra-icon': { transformBox: 'fill-box', transformOrigin: 'center' },
  '&:hover .chakra-icon': {
    animation: `${atomSpin} 1s ease-in-out infinite`,
  },
};

// Wraps NavLink with the gradient styles + the SVG gradient the icon references.
// The gradient lives in a plain <svg> (not Chakra Box) and uses userSpaceOnUse
// over the icon's 24×24 viewBox — objectBoundingBox on a stroke renders flat.
const GradientNavLink = (props: AskKulaNavLinkProps) => (
  <Box sx={gradientSx}>
    <svg
      width="0"
      height="0"
      aria-hidden="true"
      focusable="false"
      style={{ position: 'absolute' }}
    >
      <defs>
        <linearGradient
          id={ASK_KULA_GRADIENT_ID}
          x1="0"
          y1="0"
          x2="0"
          y2="24"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#8A90FF" />
          <stop offset="1" stopColor="#B15FDA" />
        </linearGradient>
      </defs>
    </svg>
    <NavLink {...props} />
  </Box>
);
interface AskKulaNavLinkProps {
  icon: React.ElementType;
  link: string;
  title: string;
  testID?: string;
  isCollapsed?: boolean;
  // Lets callers fall back to a plain NavLink (no nudge) when not applicable.
  nudgeEnabled?: boolean;
}

export const AskKulaNavLink = ({
  nudgeEnabled = true,
  ...navLinkProps
}: AskKulaNavLinkProps) => {
  const { isOpen: showNudge, dismiss: dismissNudge } =
    useDismissibleEducationalTooltip({
      storageKey: VIEWED_KEY,
      isEnabled: nudgeEnabled,
    });
  const { isNavCollapsed } = useContext(NavigationContext) ?? {};
  const isFirstCollapseRun = useRef(true);

  // The sidebar animates its width when collapsing (~0.25s), moving the anchor.
  // Chakra's Popper only repositions on scroll/resize, so pump resize events
  // across the transition window to keep the card glued to the nav item.
  useEffect(() => {
    if (!showNudge) return undefined;
    if (isFirstCollapseRun.current) {
      isFirstCollapseRun.current = false;
      return undefined;
    }
    let rafId = 0;
    let start: number | null = null;
    const DURATION = 320;
    const tick = (ts: number) => {
      if (start === null) start = ts;
      window.dispatchEvent(new Event('resize'));
      if (ts - start < DURATION) {
        rafId = requestAnimationFrame(tick);
      }
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [isNavCollapsed, showNudge]);

  if (!nudgeEnabled) {
    return <GradientNavLink {...navLinkProps} />;
  }

  return (
    <KEducationalTooltip
      anchor={
        <Box>
          <GradientNavLink {...navLinkProps} />
        </Box>
      }
      icon={Atom01}
      title="Meet Ask Kula"
      description="Your AI assistant for recruiting. Search candidates, create jobs, run salary benchmarks and more - just by asking."
      isOpen={showNudge}
      placement="right"
      onDismiss={dismissNudge}
      closeTestId="ask-kula-nudge-close"
    />
  );
};
